/**
 * Curated "shop by goal" landing pages — SEO + guided-discovery entry points
 * for visitors who don't yet know what to buy. Each slug renders at
 * `/objetivo/<slug>` via GoalLandingPage.
 */
export interface GoalConfig {
  slug: string;
  /** Short marketing name. */
  title: string;
  /** H1 used on the landing page. */
  h1: string;
  /** Hero sub-line (1 sentence). */
  tagline: string;
  /** 1-2 paragraph body for the "why this works" block. */
  description: string;
  heroAccent: string;
  emoji: string;
  /** Category names (lowercased, substring match) that map to this goal. */
  categoryKeywords: string[];
  /** Product tag values (lowercased) that map to this goal. */
  tagKeywords: string[];
  /** SEO meta */
  metaTitle: string;
  metaDescription: string;
}

export const GOAL_LANDINGS: Record<string, GoalConfig> = {
  "ganar-musculo": {
    slug: "ganar-musculo",
    title: "Ganar músculo",
    h1: "Suplementos para ganar músculo en RD",
    tagline:
      "Proteína whey, creatina y aminoácidos para acelerar fuerza e hipertrofia.",
    description:
      "Para ganar masa muscular necesitas un superávit calórico, entrenamiento de fuerza y una base sólida de proteína completa. Nuestra selección combina whey y caseína de alta biodisponibilidad, creatina monohidrato (la ayuda ergogénica con más evidencia) y aminoácidos esenciales para potenciar la síntesis proteica. Envío a todo República Dominicana y pago contra entrega disponible.",
    heroAccent: "linear-gradient(135deg,#16a34a,#0ea371)",
    emoji: "💪",
    categoryKeywords: ["protein", "proteina", "creatin", "amino", "glutamina"],
    tagKeywords: ["muscle", "musculo", "fuerza", "strength", "lean-mass", "whey", "creatina", "creatine", "post-workout"],
    metaTitle: "Ganar Músculo: Proteína y Creatina en RD | NutriMarket",
    metaDescription:
      "Stack para ganar músculo: proteína whey, creatina y aminoácidos. Marcas originales, envío a toda República Dominicana y pago contra entrega.",
  },
  "quemar-grasa": {
    slug: "quemar-grasa",
    title: "Quemar grasa",
    h1: "Suplementos para quemar grasa en RD",
    tagline:
      "Termogénicos, L-carnitina y proteína para apoyar tu definición con seguridad.",
    description:
      "Quemar grasa requiere un déficit calórico sostenido y movimiento diario. Los suplementos no reemplazan la dieta — la apoyan: termogénicos con cafeína para entrenar con más intensidad, L-carnitina para movilizar ácidos grasos y proteína para preservar masa muscular mientras pierdes peso. Te ayudamos a elegir la combinación correcta para tu meta.",
    heroAccent: "linear-gradient(135deg,#f97316,#ef4444)",
    emoji: "🔥",
    categoryKeywords: ["burner", "quemador", "preworkout", "protein", "proteina"],
    tagKeywords: ["fat-burner", "thermogenic", "energy", "low-sugar", "sugar-free", "keto-friendly", "carnitina"],
    metaTitle: "Quemar Grasa: Termogénicos y L-Carnitina en RD | NutriMarket",
    metaDescription:
      "Suplementos para definición: termogénicos, L-carnitina y proteína baja en azúcar. Originales, con envío a todo RD y pago contra entrega.",
  },
  "energia": {
    slug: "energia",
    title: "Energía",
    h1: "Suplementos para más energía en RD",
    tagline:
      "Pre-entrenos, electrolitos y vitaminas B para entrenar y rendir todo el día.",
    description:
      "La energía sostenida viene de un descanso adecuado, una hidratación inteligente y micronutrientes en orden. Combinamos pre-entrenos con cafeína dosificada para sesiones intensas, electrolitos para evitar el bajón típico del calor del Caribe y vitaminas del complejo B que tu metabolismo necesita para convertir comida en energía real.",
    heroAccent: "linear-gradient(135deg,#facc15,#f59e0b)",
    emoji: "⚡",
    categoryKeywords: ["preworkout", "electrolitos", "vitamina", "vitamins"],
    tagKeywords: ["energy", "energia", "caffeine", "pre-workout", "hydration", "electrolyte", "b-complex"],
    metaTitle: "Más Energía: Pre-Entreno y Electrolitos en RD | NutriMarket",
    metaDescription:
      "Pre-entrenos, electrolitos y vitaminas B para entrenar fuerte y rendir todo el día. Marcas originales con envío a República Dominicana.",
  },
  "recuperacion": {
    slug: "recuperacion",
    title: "Recuperación",
    h1: "Suplementos para recuperación muscular en RD",
    tagline:
      "BCAA/EAA, colágeno, magnesio y omega-3 para recuperarte más rápido entre sesiones.",
    description:
      "Recuperar bien es lo que te permite entrenar duro al día siguiente. Los aminoácidos esenciales y BCAA reducen la fatiga post-entreno, el colágeno apoya tendones y articulaciones, el magnesio mejora la calidad del sueño y el omega-3 modula la inflamación. Diseña tu protocolo de recuperación con marcas originales.",
    heroAccent: "linear-gradient(135deg,#3b82f6,#6366f1)",
    emoji: "🛌",
    categoryKeywords: ["amino", "electrolitos", "colageno", "supplements", "wellness"],
    tagKeywords: ["recovery", "recuperacion", "bcaa", "eaa", "post-workout", "hydration", "collagen", "magnesium", "omega-3"],
    metaTitle: "Recuperación: BCAA, Colágeno y Magnesio en RD | NutriMarket",
    metaDescription:
      "BCAA/EAA, colágeno, magnesio y omega-3 para recuperarte más rápido. Envío a toda República Dominicana y pago contra entrega.",
  },
};

export const GOAL_SLUGS = Object.keys(GOAL_LANDINGS);